import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Package } from 'lucide-react';
import {
  getLessons,
  getUnits,
  getClasses,
  updateLesson
} from '../firebase/lessonPlannerService';
import DashboardLayout from './DashboardLayout';
import './WeeklySchedule.css';
import './PlannerCalendar.css';

// Helper function to get the start of the week (Monday)
function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  // Get Monday: if Sunday (0), go back 6 days; otherwise go back (day - 1) days
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return monday;
}

// Helper function to get days of the week (Monday - Saturday)
function getWeekDays(weekStart) {
  const days = [];
  for (let i = 0; i < 6; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    days.push(day);
  }
  return days;
}

function WeeklySchedule() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [lessons, setLessons] = useState([]);
  const [units, setUnits] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassFilter, setSelectedClassFilter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    const [lessonsResult, unitsResult, classesResult] = await Promise.all([
      getLessons(currentUser.id),
      getUnits(currentUser.id),
      getClasses(currentUser.id)
    ]);

    if (lessonsResult.success) {
      setLessons(lessonsResult.lessons);
    }
    if (unitsResult.success) {
      setUnits(unitsResult.units);
    }
    if (classesResult.success) {
      setClasses(classesResult.classes);
    }
    setLoading(false);
  };

  const getLessonsForDate = (date) => {
    if (!date) return [];

    return lessons.filter(lesson => {
      // Check both scheduledDate and startDate
      const dateField = lesson.startDate || lesson.scheduledDate;
      if (!dateField) return false;

      const lessonDate = typeof dateField === 'string'
        ? parseDateLocal(dateField)
        : dateField.toDate
        ? dateField.toDate()
        : new Date(dateField);

      const matchesDate = lessonDate.toDateString() === date.toDateString();

      // If no class filter is selected, show all lessons
      if (!selectedClassFilter) return matchesDate;

      // Filter by class: check if the lesson's units belong to the selected class
      if (!lesson.unitIds || lesson.unitIds.length === 0) return false;

      const lessonUnits = units.filter(u => lesson.unitIds.includes(u.id));
      const belongsToClass = lessonUnits.some(unit =>
        unit.classIds && unit.classIds.includes(selectedClassFilter)
      );

      return matchesDate && belongsToClass;
    });
  };

  const getUnitTitle = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit?.title || 'No Unit';
  };

  const previousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
  };

  const nextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
  };

  const goToCurrentWeek = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to parse YYYY-MM-DD string as local date
  const parseDateLocal = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const handleDragStart = (e, lesson) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('lessonId', lesson.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const lessonId = e.dataTransfer.getData('lessonId');

    if (!lessonId || !targetDate) return;

    const lesson = lessons.find(l => l.id === lessonId);
    if (!lesson) return;

    // Format the date as YYYY-MM-DD in local timezone
    const formattedDate = formatDateLocal(targetDate);

    // Update the lesson with the new date
    const updateData = {};
    if (lesson.startDate) {
      updateData.startDate = formattedDate;
      // If it's a date range lesson, keep the duration
      if (lesson.endDate) {
        // Calculate the duration in days
        const oldStart = new Date(lesson.startDate);
        const oldEnd = new Date(lesson.endDate);
        const durationDays = Math.floor((oldEnd - oldStart) / (1000 * 60 * 60 * 24));

        // Set the new end date based on the duration
        const newEnd = new Date(targetDate);
        newEnd.setDate(newEnd.getDate() + durationDays);
        updateData.endDate = formatDateLocal(newEnd);
      }
    } else if (lesson.scheduledDate) {
      updateData.scheduledDate = formattedDate;
    }

    const result = await updateLesson(lessonId, updateData);

    if (result.success) {
      // Reload lessons to reflect the change
      await loadData();
    } else {
      alert('Failed to update lesson date');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading weekly schedule...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="weekly-schedule-container">
        <div className="page-header">
          <div>
            <h1>Weekly Schedule</h1>
            <p className="subtitle">View and manage your lessons for the week</p>
          </div>
        </div>

        <div className="weekly-view-section">
          <div className="week-controls">
            <button onClick={previousWeek} className="week-nav-btn">
              <ChevronLeft size={18} />
            </button>
            <button onClick={goToCurrentWeek} className="today-btn">
              Today
            </button>
            <button onClick={nextWeek} className="week-nav-btn">
              <ChevronRight size={18} />
            </button>
            <select
              value={selectedClassFilter || ''}
              onChange={(e) => setSelectedClassFilter(e.target.value || null)}
              className="class-filter-select"
            >
              <option value="">All Classes</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
          </div>

          <div className="week-grid">
            {getWeekDays(currentWeekStart).map((date, index) => {
              const dayLessons = getLessonsForDate(date);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNumber = date.getDate();
              const monthName = date.toLocaleDateString('en-US', { month: 'short' });

              return (
                <div
                  key={index}
                  className={`week-day-card ${isToday(date) ? 'today' : ''}`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, date)}
                >
                  <div className="week-day-header">
                    <div className="week-day-name">{dayName}</div>
                    <div className="week-day-number">
                      {monthName} {dayNumber}
                    </div>
                  </div>
                  <div className="week-day-lessons">
                    {dayLessons.length > 0 ? (
                      dayLessons.map(lesson => (
                        <div
                          key={lesson.id}
                          className="week-lesson-item"
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, lesson)}
                        >
                          <div className="week-lesson-content">
                            <div className="week-lesson-title">{lesson.title}</div>
                            {lesson.unitId && (
                              <div className="week-lesson-unit">{getUnitTitle(lesson.unitId)}</div>
                            )}
                          </div>
                          {lesson.resources && lesson.resources.length > 0 && (
                            <button
                              className="week-lesson-resource-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (lesson.resources.length === 1) {
                                  window.open(lesson.resources[0].fileUrl || lesson.resources[0].url, '_blank');
                                } else {
                                  // Navigate to lesson details or show resources menu
                                  navigate('/lessons');
                                }
                              }}
                              title={lesson.resources.length === 1 ? 'Open resource' : `${lesson.resources.length} resources`}
                            >
                              🔗
                              {lesson.resources.length > 1 && <span className="resource-badge">{lesson.resources.length}</span>}
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="week-no-lessons">No lessons</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default WeeklySchedule;
